/**
 * Coordinates the custom banner with Shopify's Customer Privacy API.
 *
 * Shopify alone persists consent, handles expiry and shares it with checkout.
 * Legacy _shopify_privacy/localStorage records are deliberately ignored: they
 * cannot prove that Shopify received a choice. Never read or mutate Shopify's
 * tracking cookies, and never call setTrackingConsent during startup.
 *
 * Consent choices (yes/no/undecided) and processing permissions are different:
 * the latter also include merchant regions, location and data-sharing opt-outs.
 * Google and optional theme trackers consume processing permissions.
 *
 * References checked 2026-10-09:
 * https://shopify.dev/docs/api/customer-privacy
 * https://developers.google.com/tag-platform/security/guides/consent
 * See docs/consent-integration-audit.txt for source-to-code checks and live checks.
 */
class ConsentManager {
  /** Create an initially denied manager; initialize() performs read-only startup. */
  constructor(options = {}) {
    // These timeouts/retry limits are application choices, not Shopify API limits.
    this.loadTimeoutMs = options.loadTimeoutMs ?? 3000;
    this.writeTimeoutMs = options.writeTimeoutMs ?? 3000;
    this.maxAttempts = options.maxAttempts ?? 3;
    this.retryDelayMs = options.retryDelayMs ?? 250;
    this._keys = ['analytics', 'marketing', 'preferences', 'sale_of_data'];
    this._choices = Object.fromEntries(this._keys.map(key => [key, '']));
    this._state = Object.fromEntries(this._keys.map(key => [key, false]));
    this._permissions = {...this._state};
    this._registered = {};
    this._bootPromise = null;
    this._initializing = null;
    this._saving = false;
    this._decisionVersion = 0;
    this._latestChoice = {};
    this._uncertainWrites = [];
    this._denialGuard = {};
    this._pendingChoice = null;
    this._pendingBaseline = null;
    this._lastGoogleConsentState = null;
    this.shopifyConsentApiReady = false;
    this._status = {phase: 'loading', error: null};

    // Subscribe before loading the API: an event can arrive during its bootstrap.
    document.addEventListener('visitorConsentCollected', () => {
      try {
        this._readShopify();
      } catch (error) {
        this._failClosed(error);
      }
    });
    // Recovery only reads Shopify; it never replays a stored visitor decision.
    window.addEventListener('online', () => { if (!this._saving) this.initialize(); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !this._saving) this.initialize();
    });
  }

  /** Check the documented API, including an API already loaded by Shopify/apps. */
  _apiAvailable() {
    const api = window.Shopify?.customerPrivacy;
    return typeof api?.currentVisitorConsent === 'function' &&
      typeof api?.setTrackingConsent === 'function';
  }

  /**
   * Load consent-tracking-api 0.1, waiting for Shopify's asynchronous bootstrap.
   * Concurrent callers share one attempt. Failed/timed-out attempts are discarded
   * so recovery can try again, and a late callback cannot revive a failed attempt.
   * @returns {Promise<boolean>} Whether the API is usable.
   */
  boot(timeoutMs = this.loadTimeoutMs) {
    if (this._apiAvailable()) {
      this.shopifyConsentApiReady = true;
      return Promise.resolve(true);
    }
    if (this._bootPromise) return this._bootPromise;

    const attempt = new Promise(resolve => {
      let finished = false;
      let requested = false;
      let poll;
      const finish = success => {
        if (finished) return;
        finished = true;
        clearTimeout(deadline);
        clearTimeout(poll);
        this.shopifyConsentApiReady = success;
        resolve(success);
      };
      const deadline = setTimeout(() => finish(false), timeoutMs);
      const check = () => {
        if (finished) return;
        if (this._apiAvailable()) return finish(true);
        if (!requested && typeof window.Shopify?.loadFeatures === 'function') {
          requested = true;
          try {
            window.Shopify.loadFeatures(
              [{name: 'consent-tracking-api', version: '0.1'}],
              error => finish(!error && this._apiAvailable())
            );
          } catch (_) {
            finish(false);
          }
        }
        if (!finished) poll = setTimeout(check, Math.min(50, timeoutMs / 3));
      };
      check();
    });
    this._bootPromise = attempt.finally(() => { this._bootPromise = null; });
    return this._bootPromise;
  }

  /**
   * Reconcile a returning visitor through read-only API calls with bounded retries.
   * No consent is recorded here, including when an old custom cookie is present.
   * @returns {Promise<boolean>} False leaves Google/theme trackers denied.
   */
  initialize() {
    if (this._initializing) return this._initializing;
    if (this._saving) return Promise.resolve(this.shopifyConsentApiReady);
    this._initializing = (async () => {
      try {
        await this._retry(async () => {
          if (!await this.boot()) throw new Error('Shopify privacy API unavailable');
          // Publish only after recovery status is reconciled; never expose an
          // old outage error alongside a successful snapshot.
          this._readShopify(false);
        });
        const needsRepair = this._pendingChoice || this._hasDenialConflict();
        this._status = {phase: needsRepair ? 'error' : 'ready', error: needsRepair ? this._status.error : null};
        this._publish();
        return true;
      } catch (error) {
        this._failClosed(error);
        return false;
      } finally {
        this._initializing = null;
      }
    })();
    return this._initializing;
  }

  /** Retry one operation a finite number of times, with a short increasing delay. */
  async _retry(operation) {
    for (let attempt = 0; attempt < this.maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        if (attempt + 1 === this.maxAttempts) throw error;
        await new Promise(resolve => setTimeout(resolve, this.retryDelayMs * (attempt + 1)));
      }
    }
  }

  /**
   * Read explicit choices and effective permissions separately; publish without
   * writing back. Allowed methods honor regions/GPC; missing methods fail closed.
   * @param {boolean} publish Defer notification while initialize reconciles status.
   */
  _readShopify(publish = true) {
    if (!this._apiAvailable()) throw new Error('Shopify privacy API unavailable');
    const api = window.Shopify.customerPrivacy;
    const consent = api.currentVisitorConsent();
    if (!consent || typeof consent !== 'object') throw new Error('Invalid Shopify consent response');
    const choices = Object.fromEntries(this._keys.map(key => [
      key, ['yes', 'no'].includes(consent[key]) ? consent[key] : ''
    ]));
    const methods = {
      analytics: 'analyticsProcessingAllowed',
      marketing: 'marketingAllowed',
      preferences: 'preferencesProcessingAllowed',
      sale_of_data: 'saleOfDataAllowed'
    };
    const permissions = Object.fromEntries(this._keys.map(key => [
      key, typeof api[methods[key]] === 'function' && api[methods[key]]() === true
    ]));
    // Commit atomically: a thrown permission read cannot leave stale grants.
    this._choices = choices;
    this._state = Object.fromEntries(this._keys.map(key => [key, choices[key] === 'yes']));
    this._permissions = permissions;
    if (this._hasDenialConflict()) {
      this._status = {phase: 'error', error: 'An earlier request changed your choices. Please save them again.'};
    } else if (!this._saving && !this._pendingChoice) {
      // A successful event/late-callback read also clears a transient outage.
      // Pending saves and unresolved withdrawals keep their existing status.
      this._status = {phase: 'ready', error: null};
    }
    this.shopifyConsentApiReady = true;
    if (publish) this._publish();
  }

  /** Keep a requested withdrawal effective until Shopify confirms a subsequent save. */
  permissions() {
    const result = {...this._permissions};
    if (this._pendingChoice) {
      for (const key of Object.keys(this._pendingChoice)) {
        // A pending grant cannot enable processing; a pending denial stops it now.
        result[key] = !!this._pendingBaseline[key] && this._pendingChoice[key] !== false && result[key];
      }
    }
    for (const key of Object.keys(this._denialGuard)) result[key] = false;
    return result;
  }

  /**
   * Display the requested selection during a failed/ambiguous save. This display
   * state never grants processing; it lets the visitor retry their intended choice.
   */
  selection() { return {...this._state, ...this._pendingChoice, ...this._denialGuard}; }

  /** Return confirmed explicit choices as booleans, never as regional permissions. */
  state() { return {...this._state}; }

  /** Return Shopify's yes/no/empty choices, preserving the unanswered state for UI. */
  choices() { return {...this._choices}; }

  /** Expose loading/saving/error/ready status without exposing mutable internal data. */
  status() { return {...this._status}; }

  /** Compatibility read-only entry point; importantly, it never calls updateConsent(). */
  async syncFromShopify() {
    await this.initialize();
    return this.state();
  }

  /**
   * Record a visitor-initiated decision only. Resolve after callback/read-back
   * confirms the requested choices; reject failures so the UI remains recoverable.
   * Data-sale consent is sent only if explicitly supplied, never inferred from ads.
   * @param {Object} newState One or more boolean consent purposes.
   * @returns {Promise<Object>} The confirmed explicit state.
   */
  async updateConsent(newState) {
    if (this._saving) throw new Error('A consent save is already in progress');
    const keys = Object.keys(newState || {});
    if (!keys.length || keys.some(key => !this._keys.includes(key) || typeof newState[key] !== 'boolean')) {
      throw new TypeError('Consent choices must be known boolean purposes');
    }
    const payload = {...newState};
    this._decisionVersion++;
    this._latestChoice = {...this._latestChoice, ...payload};
    for (const key of keys) {
      // Retain every requested withdrawal until that purpose is confirmed, even
      // if another partial save succeeds in the meantime.
      if (payload[key] === false) this._denialGuard[key] = false;
    }
    this._pendingBaseline = this.permissions();
    this._pendingChoice = payload;
    this._saving = true;
    this._status = {phase: 'saving', error: null};
    this._publish();

    try {
      await this._retry(async () => {
        if (!await this.boot()) throw new Error('Shopify privacy API unavailable');
        await this._writeShopify(payload);
      });
      // Keep restrictions until every permission read succeeds.
      this._readShopify();
      if (keys.some(key => this._choices[key] !== (payload[key] ? 'yes' : 'no'))) {
        throw new Error('An earlier consent request changed your choices');
      }
      for (const key of keys) {
        if (payload[key] || !this._uncertainWrites.some(write =>
          write.version < this._decisionVersion && write.payload[key] === true
        )) delete this._denialGuard[key];
      }
      this._pendingChoice = null;
      this._pendingBaseline = null;
      this._status = this._hasDenialConflict()
        ? {phase: 'error', error: 'A previous withdrawal still needs confirmation.'}
        : {phase: 'ready', error: null};
      this._publish();
      return this.state();
    } catch (error) {
      // Do not persist a "successful" custom choice or enable a failed acceptance.
      this._failClosed(error);
      throw error;
    } finally {
      this._saving = false;
    }
  }

  /** Compatibility write entry point. Call only in response to visitor interaction. */
  syncToShopify(state) { return this.updateConsent(state); }

  /** Compare only purposes requested in this save, preserving independent opt-outs. */
  _matchesChoice(payload) {
    const choices = window.Shopify.customerPrivacy.currentVisitorConsent();
    return Object.keys(payload).every(key => choices?.[key] === (payload[key] ? 'yes' : 'no'));
  }

  /**
   * Wrap Shopify's callback with a timeout and read-back verification. If a
   * callback is lost but the exact decision is already persisted, do not resend.
   */
  _writeShopify(payload) {
    return new Promise((resolve, reject) => {
      let settled = false;
      const request = {version: this._decisionVersion, payload: {...payload}, uncertain: false};
      const finish = (error, timedOut = false) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (timedOut) {
          // A timeout does not cancel Shopify's underlying operation, even if
          // read-back matches a choice that was already stored before this save.
          request.uncertain = true;
          this._uncertainWrites.push(request);
        }
        error ? reject(error) : resolve();
      };
      const verify = (timedOut = false) => {
        try {
          finish(this._matchesChoice(payload) ? null : new Error('Shopify did not confirm the consent choice'), timedOut);
        } catch (error) {
          finish(error, timedOut);
        }
      };
      const timer = setTimeout(() => verify(true), this.writeTimeoutMs);
      try {
        window.Shopify.customerPrivacy.setTrackingConsent(payload, result => {
          if (settled) {
            if (request.uncertain) this._readLateWrite(request);
            return;
          }
          if (result?.error) return finish(new Error(String(result.error)));
          verify();
        });
      } catch (error) {
        finish(error);
      }
    });
  }

  /** Detect an older ambiguous grant conflicting with the latest explicit denial. */
  _hasDenialConflict() {
    return Object.keys(this._denialGuard).some(key =>
      this._latestChoice[key] === false && this._choices[key] === 'yes'
    );
  }

  /**
   * Read a late callback without writing consent again. Keep Google/theme denials
   * and expose an error if Shopify was overwritten. Shopify-managed pixels cannot
   * be stopped by this local guard: the visitor must confirm a fresh save.
   */
  _readLateWrite(request) {
    try {
      this._readShopify();
    } catch (error) {
      this._failClosed(error);
    } finally {
      this._uncertainWrites = this._uncertainWrites.filter(write => write !== request);
    }
  }

  /** Deny optional processing when the API cannot be read; notify recovery UI. */
  _failClosed(error) {
    this.shopifyConsentApiReady = false;
    this._permissions = Object.fromEntries(this._keys.map(key => [key, false]));
    this._status = {phase: 'error', error: error.message};
    this._publish();
  }

  /** Publish effective permissions to Google, registered trackers and the custom UI. */
  _publish() {
    const permissions = this.permissions();
    this._updateGoogleConsentMode(permissions);
    this.pixels().forEach(name => this._tryRun(name));
    document.dispatchEvent(new CustomEvent('gct:consent-changed', {
      detail: {state: this.state(), choices: this.choices(), permissions, status: this.status()}
    }));
  }

  /**
   * Send Consent Mode v2 changes, deduplicated within this page. Ad data sharing
   * and personalization require both marketing and Shopify data-sale permission.
   * This is a conservative purpose mapping, not a Shopify-provided Google map.
   * This page bridge uses Google's documented page-code fallback for theme GTM.
   * GTM consent templates with updateConsentState are preferred when the container
   * can be configured. Shopify app pixels use Shopify's own API separately.
   */
  _updateGoogleConsentMode(permissions) {
    if (typeof gtag !== 'function') return;
    const advertisingData = permissions.marketing && permissions.sale_of_data;
    const consent = {
      analytics_storage: permissions.analytics ? 'granted' : 'denied',
      ad_storage: permissions.marketing ? 'granted' : 'denied',
      ad_user_data: advertisingData ? 'granted' : 'denied',
      ad_personalization: advertisingData ? 'granted' : 'denied',
      functionality_storage: permissions.preferences ? 'granted' : 'denied',
      personalization_storage: permissions.preferences ? 'granted' : 'denied',
      security_storage: 'granted'
    };
    const signature = JSON.stringify(consent);
    if (signature === this._lastGoogleConsentState) return;
    gtag('consent', 'update', consent);
    this._lastGoogleConsentState = signature;
  }

  /** List optional theme trackers. Shopify-managed pixels do not register here. */
  pixels() { return Object.keys(this._registered); }

  /**
   * Register a theme tracker with a purpose and mandatory withdrawal cleanup.
   * onConsent must return true after starting. onRevoke must stop collection and
   * remove owned listeners/timers. The manager cannot unload arbitrary SDKs.
   */
  register(name, {category = 'marketing', onConsent, onRevoke}) {
    if (!name || !this._keys.includes(category) || typeof onConsent !== 'function' || typeof onRevoke !== 'function') {
      throw new TypeError('Trackers require a known purpose, onConsent and onRevoke');
    }
    if (this._registered[name]) return;
    this._registered[name] = {category, onConsent, onRevoke, loaded: false};
    this._tryRun(name);
  }

  /** Start/stop a registered tracker on actual permission transitions. */
  _tryRun(name) {
    const pixel = this._registered[name];
    if (!pixel) return;
    const permissions = this.permissions();
    try {
      if (permissions[pixel.category] && !pixel.loaded) {
        pixel.loaded = pixel.onConsent(permissions) === true;
      } else if (!permissions[pixel.category] && pixel.loaded) {
        pixel.onRevoke(permissions);
        pixel.loaded = false;
      }
    } catch (error) {
      console.error('[ConsentManager] Tracker callback failed:', name, error);
    }
  }

  /** Shopify owns consent expiry; an empty purpose is unanswered or expired. */
  needsReconsent() {
    return ['analytics', 'marketing', 'preferences'].some(key => this._choices[key] === '');
  }

  /**
   * Ask for missing choices only in Shopify-configured consent regions. During
   * an outage, offer the banner while keeping optional processing denied.
   */
  async shouldShowBanner() {
    if (!this.shopifyConsentApiReady) await this.initialize();
    if (!this.shopifyConsentApiReady || this._pendingChoice) return true;
    if (this._hasDenialConflict()) return true;
    if (!this.needsReconsent()) return false;
    const api = window.Shopify.customerPrivacy;
    return typeof api.shouldShowBanner !== 'function' || api.shouldShowBanner() !== false;
  }

  /** Check whether Shopify offers a separate data-sale opt-out for this region. */
  async saleOfDataRegion() {
    if (!await this.boot()) return false;
    return window.Shopify.customerPrivacy.saleOfDataRegion?.() === true;
  }

  /** Return Shopify's detected region; an unavailable API yields an empty string. */
  async getRegion() {
    if (!await this.boot()) return '';
    return window.Shopify.customerPrivacy.getRegion?.() || '';
  }
}

// Deferred in theme.liquid after Shopify's header bootstrap. Defaults remain
// denied until this read-only reconciliation succeeds; no custom storage grant.
window.privacy = window.privacy || new ConsentManager();
window.privacy.ready = window.privacy.initialize();
